import { type ReactNode, useMemo, useState } from 'react';
import { Bars3Icon, ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import Button from '../Button/Button';
import Dropdown from '../Dropdown/Dropdown';
import DropdownButton from '../Dropdown/DropdownButton';
import DropdownItem from '../Dropdown/DropdownItem';
import DropdownItems from '../Dropdown/DropdownItems';

import HamburgerMenu from './HamburgerMenu';

import styles from './Header.module.css';

export interface HeaderNavGroup {
  label?: string;
  link?: string;
  item: ReactNode;
  _editingUrl?: string;
}

interface HeaderLanguage {
  locale: string;
  label: string;
  href: string;
}

interface HeaderProps {
  logo?: ReactNode;
  brandName?: string;
  navItems?: ReactNode[];
  menuItems?: HeaderNavGroup[];
  languages?: HeaderLanguage[];
  currentLanguage?: string;
  onLanguageChange?: (code: string) => void;
  isAuthenticated?: boolean;
  authenticatedCta?: ReactNode;
  theme?: 'light' | 'dark';
  loginLabel?: string;
  signupLabel?: string;
  logoutLabel?: string;
  onLogin?: () => void;
  onSignup?: () => void;
  onLogout?: () => void;
  className?: string;
  homepageUrl?: string;
  actionsSuffix?: ReactNode;
}

export default function Header({
  logo,
  homepageUrl = '/',
  brandName,
  menuItems = [],
  navItems = [],
  languages,
  currentLanguage,
  onLanguageChange,
  isAuthenticated = false,
  authenticatedCta,
  theme = 'light',
  loginLabel = 'Login',
  signupLabel = 'Sign up',
  logoutLabel = 'Log out',
  onLogin,
  onSignup,
  onLogout,
  className,
  actionsSuffix,
}: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const hasLanguages = !!languages && languages.length > 0;
  const activeLanguage = useMemo(
    () => (hasLanguages ? (languages.find(({ locale }) => locale === currentLanguage) ?? languages[0]) : undefined),
    [hasLanguages, currentLanguage, languages],
  );

  return (
    <>
      <header
        className={clsx(styles.header, styles[theme], className)}
        data-nav-root
      >
        <div className={styles.inner}>
          <a
            className={styles.logo}
            href={homepageUrl}
            suppressHydrationWarning
          >
            {logo}
            {!logo && brandName && <span className={styles.brandName}>{brandName}</span>}
          </a>

          {navItems?.length > 0 && (
            <nav
              className={styles.nav}
              aria-label="Primary"
            >
              {navItems}
            </nav>
          )}

          <div className={styles.actions}>
            <div className={styles.authActions}>
              {isAuthenticated ? (
                authenticatedCta || (
                  <button
                    type="button"
                    className={styles.loginLink}
                    onClick={onLogout}
                  >
                    {logoutLabel}
                  </button>
                )
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="xs"
                    highlight
                    onClick={onLogin}
                    invert={theme == 'dark'}
                    className={styles.loginLink}
                  >
                    {loginLabel}
                  </Button>
                  <Button
                    variant="primary"
                    size="xs"
                    onClick={onSignup}
                  >
                    {signupLabel}
                  </Button>
                </>
              )}
            </div>

            {hasLanguages && activeLanguage && (
              <Dropdown>
                <DropdownButton aria-label="Language">
                  <span className={styles.languageLabel}>{activeLanguage.label}</span>
                  <ChevronDownIcon
                    aria-hidden="true"
                    className={styles.languageChevron}
                  />
                </DropdownButton>
                <DropdownItems>
                  {languages.map(({ locale, label, href }) => (
                    <DropdownItem
                      key={locale}
                      active={locale === activeLanguage.locale}
                      href={href}
                      onClick={() => onLanguageChange?.(locale)}
                    >
                      {label}
                    </DropdownItem>
                  ))}
                </DropdownItems>
              </Dropdown>
            )}

            <button
              type="button"
              className={styles.hamburger}
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            >
              <Bars3Icon
                aria-hidden="true"
                className={styles.iconLg}
              />
            </button>
          </div>
        </div>
      </header>

      <HamburgerMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        menuItems={menuItems}
        isAuthenticated={isAuthenticated}
        theme={theme}
        loginLabel={loginLabel}
        signupLabel={signupLabel}
        logoutLabel={logoutLabel}
        onLogin={onLogin}
        onSignup={onSignup}
        onLogout={onLogout}
        authenticatedCta={authenticatedCta}
        actionsSuffix={actionsSuffix}
      />
    </>
  );
}
